import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { setUserProfileDetails } from 'state/app';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';
import Container from 'components/Container';
import Header from './components/Header';
import MyProducts from './components/MyProducts';
import UserInfo from './components/UserInfo';
import Nest from './components/Nest';
import validation from './validation';
import { fetchNest } from './state';
import { PRODUCTS, nomadHealthCompanyId } from './consts';

const Wrapper = styled.div`
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
`;

const Profile = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const { nest, isLoading, error } = useSelector((state) => state.global.profile.nest);
  const { user } = useSelector((state) => state.app);
  const [activeProducts, setActiveProducts] = useState([]);
  const [inactiveProducts, setInactiveProducts] = useState([]);
  const formik = useFormik({
    initialValues: useMemo(
      () => ({
        firstName: user.firstName,
        lastName: user.lastName,
        dateOfBirth: user.dateOfBirth,
        gender: user.gender,
        email: user.email
      }),
      [user]
    ),
    validationSchema: validation,
    validateOnMount: true
  });

  const onSave = (names, setIsEditMode) => {
    const userDetails = names.map((name) => ({ [name]: formik.values[name] }));

    dispatch(setUserProfileDetails(Object.assign({}, ...userDetails), setIsEditMode));
  };

  useEffect(() => {
    dispatch(fetchNest());
  }, [dispatch]);

  useEffect(() => {
    const {
      hasCompletedNI,
      /**
       * Since NH members are RH in the backend, this will be true for them too
       */
      hasCompletedRH,
      isCompanyMember,
      isCompanyAdmin,
      isCompanyOwner,
      isIndividualPlanAdmin,
      isAmbassador,
      companyId
    } = user;

    const products = PRODUCTS(isCompanyAdmin);
    const newActiveProducts = [...activeProducts];

    if (location.state?.from === '/login' && isAmbassador) {
      history.push('/ambassador/dashboard');
    }

    if (hasCompletedNI) {
      newActiveProducts.push(products.NOMAD_INSURANCE);
    }

    const isRHMember =
      !isIndividualPlanAdmin && isCompanyMember && companyId !== nomadHealthCompanyId;
    if (isRHMember || isCompanyAdmin || isCompanyOwner) {
      newActiveProducts.push(products.REMOTE_HEALTH);
    }
    /**
     * isIndividualPlanAdmin is for old Deel users, will be deprecated 180 days after Jan 2024
     */
    if (
      (hasCompletedRH || isCompanyMember) &&
      (companyId === nomadHealthCompanyId || isIndividualPlanAdmin)
    ) {
      newActiveProducts.push(products.NOMAD_HEALTH);
    }

    setActiveProducts(newActiveProducts);

    let inactiveProducts = Object.values(products).filter(
      (product) => !newActiveProducts.includes(product)
    );

    // Hide RH / NH if the user already has NH / RH since they can only have one
    // Not due to business reasons, but a technical limitation
    if (newActiveProducts.includes(products.NOMAD_HEALTH)) {
      inactiveProducts = inactiveProducts.filter(
        (product) => product !== products.REMOTE_HEALTH
      );
    } else if (newActiveProducts.includes(products.REMOTE_HEALTH)) {
      inactiveProducts = inactiveProducts.filter(
        (product) => product !== products.NOMAD_HEALTH
      );
    }

    setInactiveProducts(inactiveProducts);
  }, []); // eslint-disable-line

  return (
    <Container>
      <Header
        activeProducts={activeProducts}
        formik={formik}
        onSave={onSave}
        user={user}
        isLoading={user.isLoading}
      />
      <Wrapper>
        <MyProducts
          user={user}
          activeProducts={activeProducts}
          inactiveProducts={inactiveProducts}
        />
        <UserInfo
          activeProducts={activeProducts}
          formik={formik}
          onSave={onSave}
          isLoading={user.isLoading}
          loadingField={user.loadingField}
        />
        <Nest
          activeProducts={activeProducts}
          fetchedNest={nest}
          isLoading={isLoading}
          error={error}
        />
      </Wrapper>
    </Container>
  );
};

export default Profile;
